/* eslint-disable no-magic-numbers */
import { IBidderSettings, IBidResponse } from '../../typings/IPrebid'
import { Bidder } from '../enums'
import { AdServerTargetKey } from '../enums/AdServerTargetKey'
import { Device } from '../helpers'
import { queryParamHas } from '../helpers/getQueryParam'
import Logger from '../helpers/Logger'
import { VideoBidder } from '../enums/Bidder'

export const nativeAdPartners = ['triplelift']
export const nonSafeFramePartners: (Bidder | VideoBidder)[] = [Bidder.yieldmo]

export type DiscrepancyValues = { [bidder in Bidder]?: number }

export default class BidderSettings implements Partial<IBidderSettings> {
  standard = {
    adserverTargeting: [
      {
        key: AdServerTargetKey.hb_bidder,
        val: (bidResponse) =>
          bidResponse.s2sBidder
            ? `${bidResponse.s2sBidder}_s2s`
            : bidResponse.bidderCode
      },
      {
        key: AdServerTargetKey.hb_adid,
        val: (bidResponse) => bidResponse.adId
      },
      {
        key: AdServerTargetKey.hb_size,
        val: this.setSize
      },
      {
        key: AdServerTargetKey.hb_pb,
        val: this.transformBidToBucket
      },
      {
        key: AdServerTargetKey.hb_mediaType,
        val: (bidResponse) => bidResponse.mediaType
      },
      {
        key: AdServerTargetKey.hb_s2sBidder,
        val: (bidResponse) => bidResponse.s2sBidder
      }
    ],
    bidCpmAdjustment: this.applyDiscrepancies.bind(this)
  }

  constructor(
    private discrepancies: DiscrepancyValues | null,
    private videoDiscrepancies: DiscrepancyValues | null
  ) {}

  /**
   * Discrepancies occur in partner's bids versus the amount they actually pay out.
   * This happens for a number of reason that Erich can tell you about. We adjust incoming
   * bids with a discrepancy multiplier to account for these differences in attempt to
   * match a bid as close as possible to the expected actual amount that will be paid.
   * @param bidCpm
   * @param bidResponse
   */
  private applyDiscrepancies(
    bidCpm: number,
    bidResponse: IBidResponse
  ): number {
    // For testing individual partners, we can override a bid with ?test={partner} query params.
    // This test is hit or miss though depending on whether the partner actually bid!
    if (queryParamHas('test', bidResponse.bidder)) {
      const forcedAuctionResults = 20.0
      Logger.debug(`Forcing ${bidResponse.bidder} to win auction!`)

      return forcedAuctionResults
    }

    let adjustment = 1
    if (bidResponse.mediaType === 'video') {
      adjustment =
        (this.videoDiscrepancies &&
          this.videoDiscrepancies[bidResponse.bidder]) ||
        1
    } else {
      adjustment =
        (this.discrepancies && this.discrepancies[bidResponse.bidder]) || 1
    }
    return bidCpm * adjustment
  }

  /**
   * DFP contains Line Items ranging from increments of 0.01 between 0-20 dollar CPMs,
   * 0.25 increments between $20-$100 CPMs, and caps at $100 CPMs. However, some bids
   * come in at a higher precision than those. This function transforms the recieved bid
   * into one that will match against a Line Item in DFP.
   * @param bidResponse IBidResponse
   */
  private transformBidToBucket(bidResponse: IBidResponse): string {
    const cpm = bidResponse.cpm

    /* eslint-disable no-magic-numbers */
    if (cpm > 20.0 && cpm < 100.0) {
      return (Math.floor(cpm * 4) / 4).toFixed(2)
    }

    if (cpm >= 100.0) {
      return '100.00'
    }

    return (Math.floor(cpm * 100) / 100).toFixed(2)

    /* eslint-enable no-magic-numbers */
  }

  private setSize(bidResponse: IBidResponse): string {
    if (bidResponse.size === '1x1' || bidResponse.mediaType === 'video') {
      return '1x1'
    } else if (
      bidResponse.bidderCode === 'triplelift' &&
      bidResponse.ad &&
      bidResponse.ad.indexOf('ib.3lift.com') === -1
    ) {
      // Test disabling sf on desktop #526
      return Device.isMobileOrTablet
        ? `${bidResponse.size}_sf`
        : bidResponse.size

      // Triplelift HDX (Header Direct) runs inside regular iframes/SafeFrame based on size returned
      // return `${bidResponse.size}_sf`
    } else if (
      // Use when slot.id is restored to new implementation after script wrapper a/b testing
      // bidResponse.adUnitCode.indexOf('Content_') !== -1 &&
      bidResponse.adUnitCode.indexOf('content_') !== -1 &&
      nativeAdPartners.indexOf(bidResponse.bidderCode) !== -1
    ) {
      // Hardcode native to 1x1
      return '1x1'
    } else if (nonSafeFramePartners.indexOf(bidResponse.bidderCode) !== -1) {
      return bidResponse.size
    }

    // Test disabling sf on desktop #526
    return Device.isMobileOrTablet ? `${bidResponse.size}_sf` : bidResponse.size

    // return `${bidResponse.size}_sf`
  }
}
